/**
 * Real-time clash monitor for Room3DView.
 *
 * Detects three families of conflicts on every project edit:
 *   1) Equipment-equipment footprint overlap (within shared mount band).
 *   2) NEC 110.26(A) side-clearance violations (< MIN_SIDE_CLEARANCE_IN).
 *   3) Equipment-tray overhead collision when item top exceeds tray underside.
 *
 * Returns a memoised list of clash objects ready to feed into ValidationReport.
 * Each CLEARANCE clash carries enough metadata (partner_id, axis, gap) for the
 * v1.62 "Snap into compliance" action to compute a minimal nudge.
 */
import { useMemo } from "react";
import { orientedDimensions } from "@/lib/orientation";

const MIN_SIDE_CLEARANCE_IN = 36; // NEC 110.26(A) Cond. 1, 480V default

export default function useClashDetection(project) {
  const equipmentList = project.equipment_list || [];

  return useMemo(() => {
    const placed = equipmentList.filter((it) => it.position && it.position.x_in !== undefined && !it._unplaced);
    const out = [];

    // ── 1+2: equipment ↔ equipment ───────────────────────────────────
    for (let i = 0; i < placed.length; i += 1) {
      for (let j = i + 1; j < placed.length; j += 1) {
        const a = placed[i], b = placed[j];
        const ap = a.position, bp = b.position;
        const aOd = orientedDimensions(a);
        const bOd = orientedDimensions(b);
        const aw = aOd.wallSize,  ad = aOd.thickness;
        const bw = bOd.wallSize,  bd = bOd.thickness;

        const xOverlap = !(ap.x_in + aw <= bp.x_in || bp.x_in + bw <= ap.x_in);
        const yOverlap = !(ap.y_in + ad <= bp.y_in || bp.y_in + bd <= ap.y_in);

        const aZ0 = Number(a.mount_height_in) || 0;
        const aZ1 = aZ0 + aOd.vertSize;
        const bZ0 = Number(b.mount_height_in) || 0;
        const bZ1 = bZ0 + bOd.vertSize;
        const zOverlap = !(aZ1 <= bZ0 || bZ1 <= aZ0);

        if (xOverlap && yOverlap && zOverlap) {
          out.push({
            id: `clash-${a.id}-${b.id}-overlap`,
            item_id: a.id, partner_id: b.id, axis: null, gap: 0,
            code: "OVERLAP",
            message: `${a.tag || a.equipment_type || "EQ"} overlaps ${b.tag || b.equipment_type || "EQ"} — footprint and mount band collide.`,
            ignored: !!a.clash_ignored,
          });
          continue;
        }
        if (!zOverlap) continue; // different vertical bands → no clearance issue

        if (yOverlap) {
          const gapX = ap.x_in + aw <= bp.x_in ? bp.x_in - (ap.x_in + aw)
                      : bp.x_in + bw <= ap.x_in ? ap.x_in - (bp.x_in + bw)
                      : -1;
          if (gapX >= 0 && gapX < MIN_SIDE_CLEARANCE_IN) {
            out.push({
              id: `clash-${a.id}-${b.id}-x`,
              item_id: a.id, partner_id: b.id, axis: "x", gap: gapX,
              code: "CLEARANCE",
              message: `${a.tag || "EQ"} ↔ ${b.tag || "EQ"}: only ${gapX.toFixed(0)}" between (need ≥ ${MIN_SIDE_CLEARANCE_IN}" per NEC 110.26(A)).`,
              ignored: !!a.clash_ignored,
            });
          }
        }
        if (xOverlap) {
          const gapY = ap.y_in + ad <= bp.y_in ? bp.y_in - (ap.y_in + ad)
                      : bp.y_in + bd <= ap.y_in ? ap.y_in - (bp.y_in + bd)
                      : -1;
          if (gapY >= 0 && gapY < MIN_SIDE_CLEARANCE_IN) {
            out.push({
              id: `clash-${a.id}-${b.id}-y`,
              item_id: a.id, partner_id: b.id, axis: "y", gap: gapY,
              code: "CLEARANCE",
              message: `${a.tag || "EQ"} ↔ ${b.tag || "EQ"}: only ${gapY.toFixed(0)}" between (need ≥ ${MIN_SIDE_CLEARANCE_IN}" per NEC 110.26(A)).`,
              ignored: !!a.clash_ignored,
            });
          }
        }
      }
    }

    // ── 3: equipment ↔ tray ──────────────────────────────────────────
    const trays = project.cable_trays || [];
    const ceilingClearance = (project.tray_defaults?.ceiling_offset_in ?? 12);
    const trayMountFromCeiling = Math.max(
      0,
      (project.depth_in || 96) - ceilingClearance - (project.tray_defaults?.height_in || 4),
    );
    for (const t of trays) {
      const [x1, y1] = t.points[0] || [];
      const [x2, y2] = t.points[1] || [];
      if (x1 == null || x2 == null) continue;
      const tw = project.tray_defaults?.width_in || 12;
      const xMin = Math.min(x1, x2) - tw / 2;
      const xMax = Math.max(x1, x2) + tw / 2;
      const yMin = Math.min(y1, y2) - tw / 2;
      const yMax = Math.max(y1, y2) + tw / 2;
      for (const item of placed) {
        const ip = item.position;
        const iod = orientedDimensions(item);
        const overlapX = !(ip.x_in + iod.wallSize  <= xMin || xMax <= ip.x_in);
        const overlapY = !(ip.y_in + iod.thickness <= yMin || yMax <= ip.y_in);
        if (!overlapX || !overlapY) continue;
        const itemTop = (Number(item.mount_height_in) || 0) + iod.vertSize;
        if (itemTop > trayMountFromCeiling) {
          const trayLabel = t.id_display || t.tag || t.id || "TRAY";
          out.push({
            id: `clash-tray-${t.id}-${item.id}`,
            item_id: item.id, partner_id: null, axis: null, gap: 0,
            code: "TRAY_OVERLAP",
            message: `${item.tag || "EQ"} top at ${itemTop.toFixed(0)}" exceeds tray underside at ${trayMountFromCeiling.toFixed(0)}" — ${trayLabel} crosses overhead.`,
            ignored: !!item.clash_ignored,
          });
        }
      }
    }
    return out;
  }, [equipmentList, project.cable_trays, project.tray_defaults, project.depth_in]);
}
